import React from "react";
import Section from "./Section";

export default function HTML_Links_Colors() {
  return (
    <div className="tutorial-container">
      {/* 🔹 Title */}
      <h1 className="tutorial-title">🎨 HTML Link Colors</h1>

      {/* 🔹 Introduction */}
      <p className="tutorial-intro">
        By default, browsers display links in blue and visited links in purple.
        However, you can easily <strong>customize link colors</strong> using
        CSS. This includes normal, visited, hover, and active link states.
      </p>

      {/* 🔹 Info Box */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
          borderRadius: "12px",
          padding: "1rem",
          marginBottom: "1.5rem",
        }}
      >
        <h3
          style={{
            color: "#22c55e",
            fontWeight: 700,
            fontSize: "1.5rem",
            marginBottom: "0.5rem",
          }}
        >
          🌈 Link Pseudo-Classes
        </h3>
        <ul className="tutorial-list-points">
          <li>
            <code className="highlight_code_1">a:link</code> → normal unvisited
            link
          </li>
          <li>
            <code className="highlight_code_1">a:visited</code> → link already
            visited
          </li>
          <li>
            <code className="highlight_code_1">a:hover</code> → mouse over link
          </li>
          <li>
            <code className="highlight_code_1">a:active</code> → link when being
            clicked
          </li>
        </ul>
      </div>

      {/* Section 1️⃣ — Default Link Colors */}
      <Section
        title="1️⃣ Default Link Colors"
        html={`<a href="https://www.google.com">Default Link Example</a>`}
        description="Browsers automatically apply blue color for normal links and purple for visited ones unless customized using CSS."
      />

      {/* Section 2️⃣ — Customizing Link Colors with Inline CSS */}
      <Section
        title="2️⃣ Inline CSS for Link Colors"
        html={`<a href="https://www.example.com" style="color: green; text-decoration: none;">Green Link (No Underline)</a>`}
        description="You can use inline CSS to quickly change the color of a link or remove the underline using text-decoration: none."
      />

      {/* Section 3️⃣ — Styling Link States (Hover, Active, Visited) */}
      <Section
        title="3️⃣ Link Colors using Internal CSS"
        html={`<style>
a:link { color: blue; text-decoration: none; }
a:visited { color: purple; }
a:hover { color: red; text-decoration: underline; }
a:active { color: orange; }
</style>

<a href="https://www.wikipedia.org" target="_blank">Hover or Click this Link!</a>`}
        description="Using CSS pseudo-classes, you can define how links appear in different states — normal, visited, hover, and active."
      />

      {/* Section 4️⃣ — Multiple Styled Links */}
      <Section
        title="4️⃣ Multiple Links with Different Colors"
        html={`<style>
a.one { color: crimson; }
a.two { color: forestgreen; }
a.three { color: royalblue; }
</style>

<p><a class="one" href="#">Red Link</a></p>
<p><a class="two" href="#">Green Link</a></p>
<p><a class="three" href="#">Blue Link</a></p>`}
        description="You can apply different colors to different links using CSS classes."
      />

      {/* ✅ Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2>📘 Summary — HTML Link Colors</h2>
        <ul className="tutorial-list-points">
          <li>Use CSS to customize link colors and styles.</li>
          <li>
            Pseudo-classes like <code className="highlight_code_1">:hover</code>{" "}
            and <code className="highlight_code_1">:active</code> make links
            interactive.
          </li>
          <li>
            Always use{" "}
            <code className="highlight_code_1">text-decoration: none</code> to
            remove the underline.
          </li>
          <li>
            Use consistent colors to improve website readability and
            accessibility.
          </li>
          <li>
            Example order: <code className="highlight_code_1">a:link</code>,{" "}
            <code className="highlight_code_1">a:visited</code>,{" "}
            <code className="highlight_code_1">a:hover</code>,{" "}
            <code className="highlight_code_1">a:active</code>.
          </li>
        </ul>
      </div>
    </div>
  );
}
